<template>
    <article class="property-card">

        <!-- Property Image -->
        <div class="property-card-media">

            <a :href="productUrl" class="property-image-link">

                <!-- Member Image -->
                <template v-if="isMember && hasBaseImage">
                    <img
                        :src="baseImage"
                        :alt="product.name || 'Property Image'"
                        class="property-main-image"
                    >

                    <div class="image-overlay"></div>
                </template>

                <!-- Locked Image -->
                <template v-else>
                    <div class="property-locked-image">
                        <div class="locked-image-pattern"></div>

                        <div class="property-lock-content">
                            <div class="lock-circle">
                                <i class="las la-lock"></i>
                            </div>

                            <strong>Premium Property</strong>

                            <span>
                                Membership required to view photos
                            </span>
                        </div>
                    </div>
                </template>

            </a>

            <!-- Auction Status -->
            <span
                v-if="product.auction_status"
                class="property-status"
                :class="statusClass"
            >
                <span class="status-dot"></span>
                {{ product.auction_status }}
            </span>

            <!-- Member Badge -->
            <span
                v-if="isMember"
                class="member-badge"
            >
                <i class="las la-unlock"></i>
                Member Access
            </span>

            <!-- Property Type -->
            <span
                v-if="product.property_type"
                class="property-type-badge"
            >
                {{ product.property_type }}
            </span>

        </div>


        <!-- Property Content -->
        <div class="property-card-content">

            <!-- Property ID -->
            <div
                v-if="product.property_id"
                class="property-id-row"
            >
                <span>PROPERTY ID</span>

                <strong>
                    {{ product.property_id }}
                </strong>
            </div>


            <!-- Property Title -->
            <a
                :href="productUrl"
                class="property-title"
            >
                {{ product.name }}
            </a>


            <!-- Location -->
            <div
                v-if="product.city || product.state || product.locality"
                class="property-location"
            >
                <span class="location-icon">
                    <i class="las la-map-marker"></i>
                </span>

                <div>
                    <strong>
                        {{ product.city }}
                        <template v-if="product.state">
                            , {{ product.state }}
                        </template>
                    </strong>

                    <span v-if="product.locality">
                        {{ product.locality }}
                    </span>
                </div>
            </div>


            <!-- Auction Authority -->
            <div
                v-if="product.auction_authority"
                class="property-authority"
            >
                <div class="authority-icon">
                    <i class="las la-university"></i>
                </div>

                <div class="authority-content">
                    <span>Auction Authority</span>

                    <strong>
                        {{ product.auction_authority }}
                    </strong>
                </div>
            </div>


            <!-- MEMBER DETAILS -->
            <template v-if="isMember">

                <div class="member-details">

                    <!-- Auction Date -->
                    <div
                        v-if="product.auction_date"
                        class="detail-item"
                    >
                        <span class="detail-icon">
                            <i class="las la-calendar"></i>
                        </span>

                        <div>
                            <span class="detail-label">
                                Auction Date
                            </span>

                            <strong>
                                {{ formattedAuctionDate }}
                            </strong>
                        </div>
                    </div>


                    <!-- Auction Time -->
                    <div
                        v-if="product.auction_time"
                        class="detail-item"
                    >
                        <span class="detail-icon">
                            <i class="las la-clock"></i>
                        </span>

                        <div>
                            <span class="detail-label">
                                Auction Time
                            </span>

                            <strong>
                                {{ product.auction_time }}
                            </strong>
                        </div>
                    </div>


                    <!-- Reserve Price -->
                    <div
                        v-if="product.reserve_price"
                        class="detail-item"
                    >
                        <span class="detail-icon">
                            <i class="las la-rupee-sign"></i>
                        </span>

                        <div>
                            <span class="detail-label">
                                Reserve Price
                            </span>

                            <strong class="price-value">
                                ₹{{ formatAmount(product.reserve_price) }}
                            </strong>
                        </div>
                    </div>


                    <!-- EMD -->
                    <div
                        v-if="product.emd_amount"
                        class="detail-item"
                    >
                        <span class="detail-icon">
                            <i class="las la-wallet"></i>
                        </span>

                        <div>
                            <span class="detail-label">
                                EMD Amount
                            </span>

                            <strong>
                                ₹{{ formatAmount(product.emd_amount) }}
                            </strong>
                        </div>
                    </div>

                </div>

            </template>


            <!-- NON MEMBER LOCKED DETAILS -->
            <template v-else>

                <div class="premium-lock-box">

                    <div class="premium-lock-icon">
                        <i class="las la-lock"></i>
                    </div>

                    <div class="premium-lock-content">

                        <strong>
                            Premium Auction Details
                        </strong>

                        <span>
                            Join membership to unlock auction date,
                            reserve price, EMD and complete property details.
                        </span>

                    </div>

                </div>

            </template>


            <!-- CTA -->
            <div class="property-card-footer">

                <a
                    :href="productUrl"
                    class="property-view-button"
                >
                    <span>
                        View Property
                    </span>

                    <i class="las la-arrow-right"></i>
                </a>

            </div>

        </div>

    </article>
</template>


<script>
export default {
    props: ['product'],

    computed: {

        productUrl() {
            return route(
                'products.show',
                this.product.slug
            );
        },


        isMember() {
            return !!(
                window.FleetCart &&
                window.FleetCart.isMember === true
            );
        },


        hasBaseImage() {
            return !!(
                this.product.base_image &&
                this.product.base_image.length !== 0
            );
        },


        baseImage() {
            if (this.hasBaseImage) {
                return this.product.base_image.path;
            }

            return `${window.FleetCart.baseUrl}/themes/storefront/public/images/image-placeholder.png`;
        },


        statusClass() {
            if (!this.product.auction_status) {
                return '';
            }

            return this.product.auction_status
                .toLowerCase()
                .replace(/\s+/g, '-');
        },


        formattedAuctionDate() {
            if (!this.product.auction_date) {
                return '';
            }

            const date = new Date(
                this.product.auction_date
            );

            if (Number.isNaN(date.getTime())) {
                return this.product.auction_date;
            }

            return date.toLocaleDateString(
                'en-IN',
                {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                }
            );
        }

    },


    methods: {

        formatAmount(amount) {
            const number = Number(amount);

            if (Number.isNaN(number)) {
                return amount;
            }

            return number.toLocaleString(
                'en-IN',
                {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                }
            );
        }

    }
};
</script>


<style scoped>

.property-card {
    position: relative;
    background: #ffffff;
    border: 1px solid #e8ece9;
    border-radius: 14px;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 0 3px 14px rgba(20, 60, 35, 0.06);
    transition:
        transform 0.25s ease,
        box-shadow 0.25s ease,
        border-color 0.25s ease;
}

.property-card:hover {
    transform: translateY(-5px);
    border-color: rgba(34, 139, 75, 0.25);
    box-shadow: 0 14px 35px rgba(20, 60, 35, 0.13);
}


/* ---------------------------------
   IMAGE
--------------------------------- */

.property-card-media {
    position: relative;
    height: 245px;
    overflow: hidden;
    background: #eef3ef;
}

.property-image-link {
    display: block;
    width: 100%;
    height: 100%;
}

.property-main-image {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    transition: transform 0.45s ease;
}

.property-card:hover .property-main-image {
    transform: scale(1.045);
}

.image-overlay {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.04) 0%,
            rgba(0, 0, 0, 0) 45%,
            rgba(0, 0, 0, 0.22) 100%
        );
    pointer-events: none;
}


/* ---------------------------------
   LOCKED IMAGE
--------------------------------- */

.property-locked-image {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background:
        linear-gradient(
            135deg,
            #eef4ef 0%,
            #dce8df 50%,
            #eef4ef 100%
        );
}

.locked-image-pattern {
    position: absolute;
    inset: -50%;
    opacity: 0.35;
    background-image:
        linear-gradient(
            30deg,
            transparent 12%,
            rgba(44, 120, 70, 0.08) 12%,
            rgba(44, 120, 70, 0.08) 13%,
            transparent 13%
        );
    background-size: 34px 34px;
    transform: rotate(-8deg);
}

.property-lock-content {
    position: relative;
    z-index: 2;
    text-align: center;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.lock-circle {
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    box-shadow: 0 5px 18px rgba(20, 60, 35, 0.10);
}

.lock-circle i {
    color: #287844;
    font-size: 25px;
}

.property-lock-content strong {
    color: #183b27;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 5px;
}

.property-lock-content span {
    color: #65766b;
    font-size: 12px;
    line-height: 18px;
    max-width: 220px;
}


/* ---------------------------------
   STATUS
--------------------------------- */

.property-status {
    position: absolute;
    top: 14px;
    left: 14px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 11px;
    border-radius: 7px;
    background: #287844;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.property-status.upcoming {
    background: #287844;
}

.property-status.live {
    background: #16834a;
}

.property-status.closed,
.property-status.sold {
    background: #6d7470;
}

.property-status.cancelled {
    background: #b94343;
}


/* ---------------------------------
   MEMBER BADGE
--------------------------------- */

.member-badge {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 10px;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.94);
    color: #287844;
    font-size: 10px;
    font-weight: 700;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.10);
}

.member-badge i {
    font-size: 13px;
}


/* ---------------------------------
   PROPERTY TYPE
--------------------------------- */

.property-type-badge {
    position: absolute;
    bottom: 13px;
    left: 13px;
    z-index: 5;
    padding: 6px 10px;
    border-radius: 6px;
    background: rgba(17, 35, 24, 0.82);
    color: #ffffff;
    font-size: 10px;
    font-weight: 600;
}


/* ---------------------------------
   CONTENT
--------------------------------- */

.property-card-content {
    padding: 17px 18px 18px;
    display: flex;
    flex-direction: column;
    flex: 1;
}


/* ---------------------------------
   PROPERTY ID
--------------------------------- */

.property-id-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 7px;
}

.property-id-row span {
    color: #8b968f;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.8px;
}

.property-id-row strong {
    color: #287844;
    font-size: 10px;
    font-weight: 700;
}


/* ---------------------------------
   TITLE
--------------------------------- */

.property-title {
    display: -webkit-box;
    overflow: hidden;
    color: #17231b;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    text-decoration: none;
    margin-bottom: 10px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    transition: color 0.2s ease;
}

.property-title:hover {
    color: #287844;
}


/* ---------------------------------
   LOCATION
--------------------------------- */

.property-location {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin-bottom: 14px;
}

.location-icon {
    width: 29px;
    height: 29px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: #edf6ef;
    color: #287844;
}

.location-icon i {
    font-size: 16px;
}

.property-location > div {
    min-width: 0;
}

.property-location strong {
    display: block;
    color: #344139;
    font-size: 12px;
    font-weight: 600;
    line-height: 17px;
}

.property-location span {
    display: block;
    color: #849087;
    font-size: 11px;
    line-height: 16px;
}


/* ---------------------------------
   AUTHORITY
--------------------------------- */

.property-authority {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 0;
    border-top: 1px solid #edf0ed;
    border-bottom: 1px solid #edf0ed;
    margin-bottom: 12px;
}

.authority-icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: #f4f6f4;
    color: #287844;
}

.authority-icon i {
    font-size: 17px;
}

.authority-content {
    min-width: 0;
}

.authority-content span {
    display: block;
    color: #8b968f;
    font-size: 9px;
    font-weight: 600;
    margin-bottom: 2px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.authority-content strong {
    display: block;
    overflow: hidden;
    color: #37423b;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
}


/* ---------------------------------
   MEMBER DETAILS
--------------------------------- */

.member-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 7px;
    margin-bottom: 13px;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    padding: 8px;
    border: 1px solid #edf0ed;
    border-radius: 7px;
    background: #fbfcfb;
}

.detail-icon {
    width: 25px;
    height: 25px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: #edf6ef;
    color: #287844;
}

.detail-icon i {
    font-size: 13px;
}

.detail-item > div {
    min-width: 0;
}

.detail-label {
    display: block;
    color: #8b968f;
    font-size: 8px;
    line-height: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.detail-item strong {
    display: block;
    overflow: hidden;
    color: #26332a;
    font-size: 11px;
    line-height: 15px;
    font-weight: 700;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.detail-item .price-value {
    color: #287844;
}


/* ---------------------------------
   NON MEMBER LOCK BOX
--------------------------------- */

.premium-lock-box {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px;
    margin-bottom: 13px;
    border: 1px solid #e4ece6;
    border-radius: 8px;
    background: #f7faf8;
}

.premium-lock-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #e5f1e8;
    color: #287844;
}

.premium-lock-icon i {
    font-size: 16px;
}

.premium-lock-content {
    min-width: 0;
}

.premium-lock-content strong {
    display: block;
    color: #26392c;
    font-size: 11px;
    font-weight: 700;
    margin-bottom: 3px;
}

.premium-lock-content span {
    display: block;
    color: #7b887f;
    font-size: 9px;
    line-height: 14px;
}


/* ---------------------------------
   FOOTER / CTA
--------------------------------- */

.property-card-footer {
    margin-top: auto;
    padding-top: 1px;
}

.property-view-button {
    width: 100%;
    min-height: 43px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 10px 16px;
    border-radius: 8px;
    background: #287844;
    color: #ffffff !important;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none !important;
    transition:
        background 0.2s ease,
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.property-view-button i {
    font-size: 16px;
    transition: transform 0.2s ease;
}

.property-view-button:hover {
    background: #1f6338;
    color: #ffffff !important;
    box-shadow: 0 6px 16px rgba(40, 120, 68, 0.20);
}

.property-view-button:hover i {
    transform: translateX(3px);
}


/* ---------------------------------
   RESPONSIVE
--------------------------------- */

@media (max-width: 767px) {

    .property-card-media {
        height: 220px;
    }

    .property-card-content {
        padding: 15px;
    }

    .property-title {
        font-size: 16px;
        line-height: 22px;
    }

    .member-details {
        grid-template-columns: 1fr;
    }

}

</style>